import { StandardTableConfig } from "@stenajs-webui/grid";
import { Box, Indent, Txt } from "@stenajs-webui/core";
import { RebateTableActionMenu } from "../components/RebateTableActionMenu";
import { RebateProgress } from "@/gql/graphql";
import { IndentValues } from "@/lib/utils/IndentValues";
import { formatDateRange } from "@/lib/formatters/DateFormatter";
import { Tooltip } from "@stenajs-webui/tooltip";
import { formatPeriod } from "../lib/formatter";
import { RebateCompletionCell } from "../components/table/RebateCompletionCell";
import { CellWithTooltip } from "../components/table/CellWithTooltip";
import { RebateModelCell } from "../components/table/RebateModelCell";
import { EllipsisCell } from "@/components/ellipsis-cell/EllipsisCell";
import {
  resolvePeriodSortValue,
  resolveCompletionBackgroundColor,
} from "../lib/rebateProgressTableUtils";
import {
  getCriteriaDescriptions,
  getCriteriaNames,
  getRebateModelCode,
} from "../lib/rebateOverviewUtils";

// The table sizes its columns from their content. A hidden, wrapping copy of
// the text tells it the column wants the full text but can shrink to the
// longest word, while the visible line takes no part in sizing and ends in "…"
// only when the table runs out of room.
const TruncatedCell = ({ value }: { value: string | null | undefined }) => (
  <Box width={"100%"}>
    <Box width={"0"} minWidth={"100%"}>
      <EllipsisCell value={value} />
    </Box>
    <Box
      height={"0"}
      overflow={"hidden"}
      style={{ visibility: "hidden" }}
      aria-hidden
    >
      <Indent num={IndentValues.EIGHT}>
        <Txt>{value}</Txt>
      </Indent>
    </Box>
  </Box>
);

type RebateColumns =
  | "agreementNumber"
  | "agreementName"
  | "criteria"
  | "description"
  | "criteriaModel"
  | "period"
  | "target"
  | "expectedVolume"
  | "suggestedExpectedVolume"
  | "achievedVolume"
  | "completion"
  | "actions";

export const createTableConfig = (
  onUpdateExpectedVolume: (rebateProgress: RebateProgress) => void
): StandardTableConfig<RebateProgress, RebateColumns> => ({
  keyResolver: (item) => item.goalIds.join("_"),
  disableInfiniteList: true,
  stickyHeader: true,
  columns: {
    agreementNumber: {
      itemValueResolver: (item) =>
        item.rebateAgreement?.rebateAgreementNumber ?? "",
      columnLabel: "No.",
    },
    agreementName: {
      itemValueResolver: (item) => item.rebateAgreement?.name ?? "",
      columnLabel: "Agreement",
    },
    criteria: {
      itemValueResolver: (item) => getCriteriaNames(item),
      columnLabel: "Criteria",
      renderCell: ({ value }) => <TruncatedCell value={value} />,
    },
    description: {
      itemValueResolver: (item) => getCriteriaDescriptions(item),
      columnLabel: "Description",
      renderCell: ({ value }) => <TruncatedCell value={value} />,
    },
    criteriaModel: {
      itemValueResolver: (item) => getRebateModelCode(item),
      columnLabel: "Model",
      renderCell: ({ item, value }) => (
        <RebateModelCell rebateModelCode={value} isTopLevel={item.isTopLevel} />
      ),
      infoIconTooltipText: "Hover for code description",
    },
    period: {
      itemValueResolver: (item) =>
        resolvePeriodSortValue(item.goal?.period, item.goal?.rebateTermCode),
      columnLabel: "Period",
      renderCell: ({ item }) => {
        const valueFormatted = formatPeriod(
          item.goal?.period,
          item.goal?.rebateTermCode
        );
        const validFormatted = formatDateRange(
          item.goal?.validFrom,
          item.goal?.validTo
        );
        return (
          <CellWithTooltip
            tooltipLabel={validFormatted}
            value={valueFormatted}
          />
        );
      },
      infoIconTooltipText: "Hover for valid dates",
    },
    target: {
      itemValueResolver: (item) => {
        const first = item.displayedTarget.split("/")[0].trim();
        return Number(first) || 0;
      },
      columnLabel: "Target",
      renderCell: ({ item }) => (
        <Tooltip label={item.displayedTarget} appendTo={document.body}>
          <Indent num={IndentValues.EIGHT} maxWidth="200px">
            <Txt
              style={{ textOverflow: "ellipsis", overflow: "hidden" }}
              whiteSpace={"nowrap"}
            >
              {item.displayedTarget}
            </Txt>
          </Indent>
        </Tooltip>
      ),
    },
    expectedVolume: {
      itemValueResolver: (item) => item.expectedVolume,
      columnLabel: "Expected",
    },
    suggestedExpectedVolume: {
      itemValueResolver: (item) => item.suggestedExpectedVolume,
      columnLabel: "Suggested",
    },
    achievedVolume: {
      itemValueResolver: (item) => item.achieved,
      columnLabel: "Achieved",
    },
    completion: {
      itemValueResolver: (item) => item.completionPercent,
      columnLabel: "Target completion",
      backgroundResolver: resolveCompletionBackgroundColor,
      renderCell: ({ item }) => <RebateCompletionCell rebateProgress={item} />,
    },
    actions: {
      itemValueResolver: (item) => "",
      columnLabel: "",
      justifyContentCell: "center",
      renderCell: ({ item }) => (
        <RebateTableActionMenu
          rebate={item}
          onUpdateExpectedVolume={onUpdateExpectedVolume}
        />
      ),
    },
  },
  columnGroups: {
    agreement: {
      columnOrder: ["agreementNumber", "agreementName"],
    },
    criteria: {
      columnOrder: ["criteria", "description", "period", "criteriaModel"],
    },
    volumes: {
      borderLeft: true,
      label: "Volumes",
      columnOrder: [
        "target",
        "expectedVolume",
        "suggestedExpectedVolume",
        "achievedVolume",
      ],
    },
    status: {
      borderLeft: true,
      columnOrder: ["completion"],
    },
    actions: {
      columnOrder: ["actions"],
    },
  },
  columnGroupOrder: ["agreement", "criteria", "volumes", "status", "actions"],
});
